//#region Imports
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { faAdd } from '@fortawesome/pro-regular-svg-icons';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DeleteOutlineOutlined as DeleteIcon } from '@mui/icons-material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';

import { GlobalVariables } from '../../../globalVar';
import customAxios from '../../../utils/customAxios';
import { formatDateDDMMYYYY } from '../../../utils/dateFormat';
import { getToast } from '../../../utils/toastConfig';
import { useTranslationHook } from '../../../hooks/UseTranslationHook';
import usePersistedColumnOrder from '../../../hooks/UseCustomColumnOrder';
import { CreateFailureModal } from '../../Modal/Sigma/Failures/CreateFailureModal';
import { UpdateFailureModal } from '../../Modal/Sigma/Failures/UpdateFailureModal';
import { DeleteFailureModal } from '../../Modal/Sigma/Failures/DeleteFailuresModal';
import CustomBreadcrumbs from '../../Variety/CustomBreadcrumbs';
import { useUserPermissions } from '../../../hooks/userPermissions';
//#endregion

const LOCAL_COLUMN_WIDTH = GlobalVariables.getDefaultColumnWidth();

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

export const FailuresTable = () => {
  const userPermissions = useUserPermissions();

  const [isFetching, setIsFetching] = useState(false); // State for tracking API request status
  const [showCreateModal, setShowCreateModal] = useState(false); // State for showing the create modal
  const [showUpdateModal, setShowUpdateModal] = useState(false); // State for showing the update modal
  const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing the delete modal
  const [failures, setFailures] = useState([]);
  const [failureId, setFailureId] = useState(null);
  const [failureType, setFailureType] = useState({ id: '', name: '' });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    addString,
    editString,
    deleteString,
    statusString,
    createdAtString,
    createdByString,
    updatedAtString,
    updatedByString,
    notesString,
  } = useTranslationHook();

  const { id } = useParams();

  // Define initial table columns
  const initialColumns = [
    { field: 'id', headerName: 'ID', width: LOCAL_COLUMN_WIDTH },
    {
      field: 'description',
      headerName: 'Descripción de la falla',
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'name',
      headerName: 'Nombre falla SN',
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'level',
      headerName: 'Nivel',
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'sla_hours',
      headerName: 'Horas',
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'is_active',
      headerName: statusString,
      width: 150,
      renderCell: (params) => {
        if (
          params.row.is_active === 'Activo' ||
          params.row.is_active === 'Active'
        ) {
          return (
            <div>
              <span className="statusActive">{params.row.is_active}</span>
            </div>
          );
        } else {
          return (
            <div>
              <span className="statusInactive">{params.row.is_active}</span>
            </div>
          );
        }
      },
      valueGetter: (params) => params.row.is_active,
    },
    {
      field: 'created_at',
      headerName: createdAtString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'created_by_name',
      headerName: createdByString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'updated_at',
      headerName: updatedAtString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'updated_by_name',
      headerName: updatedByString,
      width: LOCAL_COLUMN_WIDTH,
    },
    {
      field: 'notes',
      headerName: notesString,
      width: LOCAL_COLUMN_WIDTH,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            ['created_at', 'updated_at'].includes(col.field)
              ? formatDateDDMMYYYY(params.row[col.field])
              : params.row[col.field] || '-',
        },
  );

  // Define the action column with edit and delete buttons
  const actionColumn = [
    {
      field: 'action',
      headerName: 'Detalle',
      width: 160,
      renderCell: (params) => {
        return (
          <div
            className="cellAction equips-datagrid-actions-tour"
            style={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Edit Button */}
            {userPermissions.includes('Update Failures') && (
              <Tooltip title={editString} arrow>
                <IconButton
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                  onClick={() => {
                    setFailureId(params.row.id); // Set selected failure type IDID
                    setShowUpdateModal(true); // Show the update modal
                  }}
                >
                  <ModeEditOutlineOutlinedIcon
                    className="detail-icons"
                    id="update-icon"
                  />
                </IconButton>
              </Tooltip>
            )}
            {/* Delete Button */}
            {userPermissions.includes('Delete Failures') && (
              <Tooltip title={deleteString} arrow>
                <IconButton
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                  onClick={() => {
                    setFailureId(params.row.id); // Set selected failure type ID
                    setShowDeleteModal(true); // Show the delete modal
                  }}
                >
                  <DeleteIcon className="detail-icons" id="delete-icon" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const [columns, updateColumnOrder] = usePersistedColumnOrder(
    'failuresTable',
    initialColumns,
  );

  const fetchFailureType = async () => {
    const endpoint = `${GlobalVariables.getURL()}failure-types/${id}`;
    const response = await customAxios.get(endpoint, HEADERS);

    if (response.status === 204) return { id: '', name: '' };
    return response.data.data;
  };

  const fetchFailures = async () => {
    const endpoint = `${GlobalVariables.getURL()}failures/filter/${id}`;
    const response = await customAxios.get(endpoint, HEADERS);

    if (response.status === 204) return [];
    const failuresData = response.data.data.map((f) => {
      return {
        ...f,
        is_active: t(f.is_active === 1 ? 'Activo' : 'Inactivo'),
      };
    });
    return failuresData;
  };

  const fetchData = async () => {
    setIsFetching(true);
    try {
      const [failureTypesData, failuresData] = await Promise.all([
        fetchFailureType(),
        fetchFailures(),
      ]);

      setFailureType(failureTypesData);
      setFailures(failuresData);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  /**
   * Handles column order changes for the table.
   * @param {Object} params - Parameters containing target and old index.
   */
  const handleColumnOrderChange = (params) => {
    const { targetIndex, oldIndex } = params;

    const updatedColumns = [...columns];
    const [movedColumn] = updatedColumns.splice(oldIndex, 1);
    updatedColumns.splice(targetIndex, 0, movedColumn);

    updateColumnOrder(updatedColumns); // Update column order in persisted state
  };

  // Check if user has any action permissions
  const hasActionPermissions =
    userPermissions.includes('Update Failures') ||
    userPermissions.includes('Delete Failures');

  return (
    <>
      <CustomBreadcrumbs />

      <div className="datatable-btns-container">
        <div className="dt-btn-main-container">
          <span className="dabiWebText">Fallas</span>
          {userPermissions.includes('Create Failures') && (
            <button
              className="add-button create-equip-tour"
              onClick={() => setShowCreateModal(true)}
            >
              {addString}
              <FontAwesomeIcon icon={faAdd} />
            </button>
          )}
        </div>
      </div>

      {/* Failure type header */}
      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={6}>
          {isFetching ? (
            <Skeleton variant="rounded" height={40} />
          ) : (
            <TextField
              size="small"
              fullWidth
              disabled
              label="ID"
              value={failureType.id}
            />
          )}
        </Grid>
        <Grid item xs={6}>
          {isFetching ? (
            <Skeleton variant="rounded" height={40} />
          ) : (
            <TextField
              size="small"
              fullWidth
              disabled
              label="Tipo de servicio"
              value={failureType.name}
            />
          )}
        </Grid>
      </Grid>

      {/* Create Modal */}
      <CreateFailureModal
        failure_type_id={id}
        showModal={showCreateModal}
        setShowModal={setShowCreateModal}
        refreshData={fetchData}
      />

      {/* Update Modal */}
      <UpdateFailureModal
        failure_type_id={id}
        failureId={failureId}
        showModal={showUpdateModal}
        setShowModal={setShowUpdateModal}
        refreshData={fetchData}
      />

      {/* Delete Modal */}
      <DeleteFailureModal
        failureId={failureId}
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        refreshData={fetchData}
      />

      {/* Data Grid */}
      <DataGridPro
        rows={failures}
        columns={hasActionPermissions ? columns.concat(actionColumn) : columns}
        loading={isFetching}
        onColumnOrderChange={(params) => handleColumnOrderChange(params)}
        initialState={{
          sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }],
          },
          pinnedColumns: hasActionPermissions ? { right: ['action'] } : {},
        }}
        slots={{
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              No hay fallas en la base de datos
            </Stack>
          ),
          toolbar: GridToolbar, // Toolbar with default functionality
        }}
      />
    </>
  );
};
